import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Music, Heart, Star, Users, Camera, ExternalLink, Disc3 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { services, testimonials, portfolioItems } from '../data/mock';

const JSON_LD = {
  "@context": "https://schema.org",
  "@graph": [
    {
      "@type": "Organization",
      "@id": "https://sax-dj.fr/#organization",
      "name": "Sax&DJ",
      "url": "https://sax-dj.fr",
      "logo": "https://sax-dj.fr/saxDJLOGO.webp",
      "telephone": "+33661944226",
      "email": "contact@sax-dj.fr",
      "address": {
        "@type": "PostalAddress",
        "addressRegion": "Île-de-France",
        "addressCountry": "FR"
      },
      "areaServed": ["Rambouillet", "Chartres", "Yvelines", "Île-de-France"],
      "sameAs": [
        "https://www.mariages.net",
        "https://www.eventigo.eu/artist-profile/sax-dj"
      ]
    },
    {
      "@type": "LocalBusiness",
      "@id": "https://sax-dj.fr/#localbusiness",
      "name": "Sax&DJ",
      "description": "Saxophoniste et DJ professionnel pour mariages et événements en Île-de-France. Formule Sax+DJ, DJ soirée, saxophone cocktail. Devis sur-mesure.",
      "url": "https://sax-dj.fr",
      "telephone": "+33661944226",
      "email": "contact@sax-dj.fr",
      "image": "https://sax-dj.fr/saxDJLOGO.webp",
      "priceRange": "€€€",
      "address": {
        "@type": "PostalAddress",
        "addressLocality": "Rambouillet",
        "addressRegion": "Île-de-France",
        "addressCountry": "FR"
      },
      "geo": {
        "@type": "GeoCoordinates",
        "latitude": 48.6445,
        "longitude": 1.8305
      },
      "areaServed": [
        { "@type": "City", "name": "Rambouillet" },
        { "@type": "City", "name": "Chartres" },
        { "@type": "AdministrativeArea", "name": "Yvelines" },
        { "@type": "AdministrativeArea", "name": "Île-de-France" }
      ],
      "hasOfferCatalog": {
        "@type": "OfferCatalog",
        "name": "Prestations musicales",
        "itemListElement": [
          { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "Formule Sax + DJ" } },
          { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "DJ Soirée" } },
          { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "Saxophone Cocktail" } },
          { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "Soirée d'entreprise clé en main" } }
        ]
      }
    },
    {
      "@type": "WebSite",
      "@id": "https://sax-dj.fr/#website",
      "url": "https://sax-dj.fr",
      "name": "Sax&DJ",
      "description": "Saxophone & DJ pour mariages et événements en Île-de-France",
      "publisher": { "@id": "https://sax-dj.fr/#organization" },
      "inLanguage": "fr-FR"
    }
  ]
};

export const Home = () => {
  const featuredServices = services.filter(service => service.highlight || services.indexOf(service) < 3);
  const heroImage = portfolioItems.find(item => item.category === 'hero-accueil');
  const displayTestimonials = testimonials.slice(0, 3);

  useEffect(() => {
    document.title = "Sax&DJ | Saxophone & DJ — Mariages et Événements en Île-de-France";

    const setMeta = (attr, name, content) => {
      let el = document.querySelector(`meta[${attr}="${name}"]`);
      if (!el) { el = document.createElement('meta'); el.setAttribute(attr, name); document.head.appendChild(el); }
      el.setAttribute('content', content);
    };

    const setLink = (rel, href) => {
      let el = document.querySelector(`link[rel="${rel}"]`);
      if (!el) { el = document.createElement('link'); el.setAttribute('rel', rel); document.head.appendChild(el); }
      el.setAttribute('href', href);
    };

    setMeta('name', 'description', "Sax&DJ : saxophoniste et DJ professionnel pour mariages et événements en Île-de-France. Formule Sax+DJ, DJ soirée, saxophone cocktail. Zone Rambouillet, Chartres, Yvelines (78). Devis gratuit.");
    setLink('canonical', 'https://sax-dj.fr/');
    setMeta('property', 'og:url',   'https://sax-dj.fr/');
    setMeta('property', 'og:title', "Sax&DJ | Saxophone & DJ — Mariages et Événements en Île-de-France");

    let script = document.getElementById('jsonld-home');
    if (!script) {
      script = document.createElement('script');
      script.type = 'application/ld+json';
      script.id   = 'jsonld-home';
      document.head.appendChild(script);
    }
    script.textContent = JSON.stringify(JSON_LD);

    return () => {
      const canonical = document.querySelector('link[rel="canonical"]');
      if (canonical) canonical.remove();
      const jsonld = document.getElementById('jsonld-home');
      if (jsonld) jsonld.remove();
    };
  }, []);

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-ivory via-pearl to-light-gold/20 py-20 md:py-32 overflow-hidden">
        {/* Hero Background Image */}
        {heroImage && heroImage.image_url && (
          <div 
            className="absolute inset-0 bg-cover bg-center bg-no-repeat"
            style={{ backgroundImage: `url(${heroImage.image_url})` }}
          >
            <div className="absolute inset-0 bg-white/80 backdrop-blur-[1px]"></div>
          </div>
        )}
        {!heroImage && (
          <div className="absolute inset-0 bg-white/60 backdrop-blur-[1px]"></div>
        )}
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="max-w-4xl mx-auto">
            <h1 className="text-4xl md:text-6xl font-bold text-charcoal mb-6 leading-tight">
              L'alliance parfaite entre{' '}
              <span className="bg-gradient-to-r from-blue-900 to-blue-700 bg-clip-text text-transparent block mt-2">
                Saxophone & DJ
              </span>
            </h1>
            <p className="text-xl text-warm-gray mb-8 max-w-2xl mx-auto leading-relaxed">
              Créez des moments inoubliables pour votre mariage avec une expérience musicale unique, 
              de la cérémonie à la soirée dansante.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button 
                size="lg"
                asChild
                className="bg-blue-900 hover:bg-blue-800 text-white text-lg px-8 py-4 hover:shadow-lg transition-all duration-300"
                data-testid="hero-cta-devis"
              >
                <Link to="/contact">
                  Demander un devis
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Link>
              </Button>
              <Button 
                variant="outline" 
                size="lg"
                asChild
                className="border-2 border-blue-900 text-blue-900 hover:bg-blue-900 hover:text-white text-lg px-8 py-4 transition-all duration-300"
                data-testid="hero-cta-portfolio"
              >
                <Link to="/portfolio">
                  Voir nos réalisations
                </Link>
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {[
              { number: '10+', label: 'Années d\'expérience', icon: Music },
              { number: '50+', label: 'Mariages premium', icon: Heart },
              { number: '5', label: 'Étoiles en moyenne', icon: Star },
              { number: '100%', label: 'Clients satisfaits', icon: Users }
            ].map((stat, index) => (
              <div key={index} className="text-center group">
                <div className="bg-gradient-to-br from-ivory to-pearl p-4 rounded-full w-16 h-16 mx-auto mb-4 group-hover:shadow-lg transition-all duration-300">
                  <stat.icon className="h-8 w-8 text-gold mx-auto" />
                </div>
                <div className="text-3xl font-bold text-charcoal mb-2">{stat.number}</div>
                <div className="text-warm-gray text-sm">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Services Preview */}
      <section className="py-20 bg-gradient-to-br from-ivory/30 to-pearl/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-charcoal mb-4">
              Nos Services
            </h2>
            <p className="text-xl text-warm-gray max-w-2xl mx-auto">
              Une palette complète de services musicaux pour accompagner chaque moment de votre événement
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {featuredServices.map((service) => (
              <Card key={service.id} className="group hover:shadow-xl transition-all duration-300 border-0 bg-white/80 backdrop-blur-sm">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between mb-4">
                    <div>
                      <h3 className="text-xl font-bold text-charcoal group-hover:text-navy-blue transition-colors">
                        {service.title}
                      </h3>
                      <p className="text-sm text-gold font-medium">{service.subtitle}</p>
                    </div>
                    {service.highlight && (
                      <div className="bg-gradient-to-r from-navy-blue to-royal-blue text-white px-3 py-1 rounded-full text-xs font-medium">
                        Premium
                      </div>
                    )}
                  </div>
                  <p className="text-warm-gray mb-4 leading-relaxed">{service.description}</p>
                  <ul className="space-y-2 mb-6">
                    {service.features.slice(0, 3).map((feature, index) => (
                      <li key={index} className="flex items-center text-sm text-charcoal">
                        <div className="w-1.5 h-1.5 bg-gold rounded-full mr-3"></div>
                        {feature}
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            ))}
          </div>

          <div className="text-center mt-12">
            <Button 
              asChild
              variant="outline"
              size="lg"
              className="border-2 border-navy-blue text-navy-blue hover:bg-navy-blue hover:text-white"
            >
              <Link to="/services">
                Voir tous nos services
                <ArrowRight className="ml-2 h-4 w-4" />
              </Link>
            </Button>
            
            {/* Badges Partenaires */}
            <div className="flex items-center justify-center space-x-8 mt-12">
              {/* Badge Mariages.net */}
              <a 
                href='https://www.mariages.net' 
                target='_blank' 
                rel="noopener noreferrer"
                className="hover:opacity-80 transition-opacity"
                title='Mariages.net'
              >
                <img 
                  src='https://www.mariages.net/images/sellos/label-partenaire--gg321472.png' 
                  alt="Sax&DJ partenaire officiel Mariages.net — saxophone et DJ pour mariages"
                  className="h-20 md:h-24"
                />
              </a>
              
              {/* Badge Eventigo */}
              <a 
                href='https://www.eventigo.eu/artist-profile/sax-dj' 
                target='_blank' 
                rel="noopener noreferrer"
                className="hover:opacity-80 transition-opacity"
              >
                <img 
                  src='https://www.eventigo.eu/img/front/badges/badge-eventigo-rond-noir-150px.png' 
                  alt='Sax&DJ – Partenaire de Eventigo'
                  title='Sax&DJ – Partenaire de Eventigo'
                  className="h-20 w-20 md:h-24 md:w-24"
                />
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-charcoal mb-4">
              Ils nous font confiance
            </h2>
            <p className="text-xl text-warm-gray">
              Découvrez les témoignages de nos clients
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {displayTestimonials.map((testimonial) => (
              <Card key={testimonial.id} className="border-0 bg-gradient-to-br from-ivory/50 to-pearl/50 hover:shadow-lg transition-all duration-300">
                <CardContent className="p-6">
                  <div className="flex items-center mb-4">
                    {[...Array(testimonial.rating)].map((_, i) => (
                      <Star key={i} className="h-4 w-4 text-gold fill-current" />
                    ))}
                  </div>
                  <p className="text-charcoal mb-4 italic leading-relaxed">
                    "{testimonial.comment}"
                  </p>
                  <div className="border-t border-warm-gray/20 pt-4">
                    <p className="font-semibold text-charcoal">{testimonial.name}</p>
                    <p className="text-sm text-warm-gray">{testimonial.event}</p>
                    <p className="text-xs text-gold">{testimonial.date}</p>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          <div className="text-center mt-12">
            <Button 
              asChild
              variant="outline"
              size="lg"
              className="border-2 border-navy-blue text-navy-blue hover:bg-navy-blue hover:text-white"
            >
              <Link to="/portfolio">
                Voir plus de témoignages
                <ArrowRight className="ml-2 h-4 w-4" />
              </Link>
            </Button>
          </div>
        </div>
      </section>

      {/* Collaborateurs */}
      <section className="py-20 bg-gradient-to-br from-ivory/30 to-pearl/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-14">
            <h2 className="text-3xl md:text-4xl font-bold text-charcoal mb-4">
              Nos collaborateurs
            </h2>
            <p className="text-lg text-warm-gray max-w-2xl mx-auto">
              Des professionnels sélectionnés avec soin pour sublimer chaque instant de votre mariage.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-4xl mx-auto">
            {/* Raphaëlle FAVA */}
            <div className="bg-white rounded-2xl shadow-sm hover:shadow-lg transition-all duration-300 p-8 flex items-start space-x-5">
              <div className="flex-shrink-0">
                <img
                  src="/Rapha.webp"
                  alt="Raphaëlle FAVA — photographe mariage spécialisée reportage de couple"
                  className="h-20 w-20 rounded-xl object-cover"
                />
              </div>
              <div>
                <p className="text-xs font-semibold text-gold uppercase tracking-widest mb-1">Photographie</p>
                <h3 className="text-xl font-bold text-charcoal mb-3">Raphaëlle FAVA</h3>
                <p className="text-warm-gray leading-relaxed text-sm mb-4">
                  Spécialiste du reportage photo de couple, Raphaëlle apporte sa touche unique grâce à son passé dans la mode.
                  Après 7 ans dans les coulisses de tournages et de séances photo en tant que make-up artist,
                  elle travaille aujourd'hui derrière l'objectif pour des mariés soucieux d'obtenir des souvenirs uniques du plus beau jour de leur vie.
                </p>
                <a
                  href="https://www.raphaellefava.com/mariages"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center text-blue-900 font-semibold hover:text-gold transition-colors duration-200 text-sm"
                >
                  Voir son travail
                  <ExternalLink className="ml-1.5 h-3.5 w-3.5" />
                </a>
              </div>
            </div>

            {/* FG Events */}
            <div className="bg-white rounded-2xl shadow-sm hover:shadow-lg transition-all duration-300 p-8 flex items-start space-x-5">
              <div className="flex-shrink-0">
                <img
                  src="/Flo.webp"
                  alt="DJ Florian Groovel — FG Events, DJ deep house et électro pour mariages et soirées"
                  className="h-20 w-20 rounded-xl object-cover"
                />
              </div>
              <div>
                <p className="text-xs font-semibold text-gold uppercase tracking-widest mb-1">DJ</p>
                <h3 className="text-xl font-bold text-charcoal mb-3">FG Events</h3>
                <p className="text-warm-gray leading-relaxed text-sm mb-4">
                  Pour vos soirées privées ou professionnelles, FG Events a le DJ qu'il vous faut.
                  Aux manettes, DJ Florian Groovel sélectionne pour vous le meilleur de l'électro deep, Lounge, Chill-Out et Deep House,
                  agrémentée de petites touches acoustiques dont lui seul a le secret.
                  Cocktails, séminaires, lancements de produits, défilés de mode, fêtes somptueuses...
                </p>
                <a
                  href="https://www.mariages.net/musique-mariage/fg-events--e218853"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center text-blue-900 font-semibold hover:text-gold transition-colors duration-200 text-sm"
                >
                  Voir son travail
                  <ExternalLink className="ml-1.5 h-3.5 w-3.5" />
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-navy-blue to-royal-blue">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
            Prêt à créer des souvenirs inoubliables ?
          </h2>
          <p className="text-xl text-white/90 mb-8 leading-relaxed">
            Discutez de votre projet avec nous. Devis personnalisé, gratuit, sans engagement.
          </p>
          <Button 
            size="lg"
            asChild
            className="bg-white text-navy-blue hover:bg-ivory hover:text-charcoal text-lg px-8 py-4 hover:shadow-lg transition-all duration-300"
            data-testid="cta-devis-gratuit"
          >
            <Link to="/contact">
              Demander un devis gratuit
              <ArrowRight className="ml-2 h-5 w-5" />
            </Link>
          </Button>
        </div>
      </section>
    </div>
  );
};
